import React, { useEffect, useState } from 'react';
import BunkerRegStatusList from './BunkerRegStatusList';
import { BunkeringRegStatusWrapper } from '../../Bunkering.styles';
import BunkerRegStatusChange from './BunkerRegStatusChange';
import { useAuthStore } from 'src/context/AuthContext';
import useGetBunkeringData from 'src/api/bunkering/useGetBunkeringData';
import { BunkeringStatus } from 'src/types/ViewModel/Bunkering/BunkeringStatus';
import { BunkeringStatusEnum } from 'src/types/ViewModel/Bunkering/BunkeringEvent';
import { useBunkerContext } from 'src/context/BunkerContext';
import { mapBunkering } from 'src/factory/Bunkering/BunkeringFactory';
import { validateLiterWeight, verifyAllFieldsValid } from './BunkerRegStatusUtils';
import BunkerRegStatusChangeModal from './BunkerRegStatusChangeModal';
import { useAppStore } from 'src/context/AppContext';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import { isDateBefore, subtractDateDays } from 'odfjell-date-time-utils/dateTimeUtils';

const BunkerRegStatusContainer: React.FC = () => {
    const { loggedInUser } = useAuthStore();
    const { selectedBunkering, selectedBunkeringEvent } = useBunkerContext();
    const { upsertBunkeringStatus, deleteBunkeringStatus, upsertBunkering } = useGetBunkeringData();
    const { appFeatureSettings } = useAppStore();
    const [newStatus, setNewStatus] = useState<BunkeringStatus>();
    const [invalidFields, setInvalidFields] = useState<string[]>([]);
    const [validLiterWeight, setValidLiterWeight] = useState<boolean>(false);
    const [statusChangeModalOpen, setStatusChangeModalOpen] = useState<boolean>(false);

    const [featureEnabledDate, setFeatureEnabledDate] = useState<Date | null>(null);

    useEffect(() => {
        if (appFeatureSettings && appFeatureSettings.length > 0) {
            const feature = appFeatureSettings.find((a) => a.appFeatureId === AppFeatureEnum.Bunkering);
            const featureVessel = feature?.vessels.find((v) => v.imoNumber === selectedBunkering.imoNumber.toString());
            if (featureVessel && featureVessel.fromDate) {
                setFeatureEnabledDate(featureVessel.fromDate);
            } else {
                setFeatureEnabledDate(null);
            }
        }
    }, [appFeatureSettings, selectedBunkering]);

    useEffect(() => {
        setNewStatus(null);
        setInvalidFields(verifyAllFieldsValid(selectedBunkering));
        setValidLiterWeight(validateLiterWeight(selectedBunkering));
    }, [selectedBunkering]);

    const initiateStatusChange = (status: BunkeringStatusEnum) => {
        let activeDate = new Date();
        if (featureEnabledDate && selectedBunkering.bunkeringDateTime && isDateBefore(selectedBunkering.bunkeringDateTime, featureEnabledDate)) {
            activeDate = subtractDateDays(featureEnabledDate, 1);
        }
        setNewStatus({
            bunkeringStatusId: 0,
            bunkeringStatusGuid: window.crypto.randomUUID(),
            bunkeringGuid: selectedBunkering.bunkeringGuid,
            bunkeringId: selectedBunkering?.bunkeringId ?? 0,
            status: status,
            activeFromDate: activeDate,
            updatedAt: new Date(),
            updatedBy: loggedInUser?.userName ?? loggedInUser?.appAccessToken,
        });
        setStatusChangeModalOpen(true);
    };

    const handleExistingDateChange = (status: BunkeringStatus, date: Date) => {
        status.activeFromDate = date;
        status.updatedAt = new Date();
        status.updatedBy = loggedInUser?.userName ?? loggedInUser?.appAccessToken;
        upsertBunkeringStatus(status, selectedBunkering.imoNumber);
    };

    const handleDeleteStatus = (bunkeringStatus: BunkeringStatus) => {
        deleteBunkeringStatus(bunkeringStatus.bunkeringStatusId, bunkeringStatus.bunkeringStatusGuid, selectedBunkering.imoNumber);

        if (bunkeringStatus.status === BunkeringStatusEnum.Approved) {
            selectedBunkering.approvedForUseAt = null;
            selectedBunkering.bunkeringStatuses = selectedBunkering.bunkeringStatuses.filter(
                (status) => status.status !== BunkeringStatusEnum.Approved
            );
            selectedBunkering.lastUpdatedAt = new Date();
            selectedBunkering.lastUpdatedBy = loggedInUser.userName ?? loggedInUser.appAccessToken;
            const bunkeringBM = mapBunkering(selectedBunkering);
            upsertBunkering(selectedBunkering.imoNumber, bunkeringBM);
        }
    };

    const handleDateChange = (date: Date) => {
        setNewStatus({
            ...newStatus,
            activeFromDate: date,
        });
    };

    const handleSaveStatus = () => {
        if (newStatus) {
            if (newStatus.status === BunkeringStatusEnum.Approved) {
                selectedBunkering.approvedForUseAt = newStatus.activeFromDate;
                selectedBunkering.bunkeringStatuses.push(newStatus);
                selectedBunkering.lastUpdatedAt = new Date();
                selectedBunkering.lastUpdatedBy = loggedInUser.userName ?? loggedInUser.appAccessToken;
                const bunkeringBM = mapBunkering(selectedBunkering);
                upsertBunkering(selectedBunkering.imoNumber, bunkeringBM);
            } else {
                upsertBunkeringStatus(newStatus, selectedBunkering.imoNumber);
            }
        }
    };

    return (
        <BunkeringRegStatusWrapper className={selectedBunkeringEvent && !selectedBunkeringEvent?.debunkering ? 'expanded' : ''}>
            <BunkerRegStatusChangeModal
                isOpen={statusChangeModalOpen}
                selectedBunkering={selectedBunkering}
                newStatus={newStatus}
                onUpdateDate={handleDateChange}
                onClose={() => setStatusChangeModalOpen(false)}
                onSave={handleSaveStatus}
            />
            <BunkerRegStatusChange
                bunkering={selectedBunkering}
                invalidFields={invalidFields}
                validLiterWeight={validLiterWeight}
                featureEnabledDate={featureEnabledDate}
                initiateStatusChange={initiateStatusChange}
            />
            <BunkerRegStatusList
                bunkering={selectedBunkering}
                handleDateChange={handleExistingDateChange}
                deleteBunkeringStatus={handleDeleteStatus}
            />
        </BunkeringRegStatusWrapper>
    );
};

export default BunkerRegStatusContainer;
